import { useEffect, useState } from 'react'
import { Alert, App, Form, Modal, Select, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import type { PanelUser } from '@/features/identity/identityApi'
import { fetchCenters, fetchUserNetwork, fetchVisps, saveUserNetwork } from './networkApi'

interface UserNetworkModalProps {
  open: boolean
  user: PanelUser | null
  onClose: () => void
}

/** Xodimga VISP va Center biriktirish (ADR-049): bo'sh qolsa filial doirasi amal qiladi */
export function UserNetworkModal({ open, user, onClose }: UserNetworkModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const userId = user?.id ?? ''

  const current = useQuery({
    queryKey: queryKeys.network.userAssignment(userId),
    queryFn: () => fetchUserNetwork(userId),
    enabled: open && userId !== '',
  })
  const visps = useQuery({ queryKey: queryKeys.network.visps, queryFn: fetchVisps, enabled: open })
  const centers = useQuery({ queryKey: queryKeys.network.centers, queryFn: fetchCenters, enabled: open })

  const [vispIds, setVispIds] = useState<string[]>([])
  const [centerIds, setCenterIds] = useState<string[]>([])

  useEffect(() => {
    if (!open || !current.data) return
    setVispIds([...current.data.vispIds])
    setCenterIds([...current.data.centerIds])
  }, [open, current.data])

  const save = useMutation({
    mutationFn: () => saveUserNetwork(userId, { vispIds, centerIds }),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.network.userAssignment(userId), saved)
      void message.success(t('network.userAssignment.saved'))
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  const loadError = current.error ?? visps.error ?? centers.error
  const ready = current.isSuccess && visps.isSuccess && centers.isSuccess

  return (
    <Modal
      open={open}
      title={t('network.userAssignment.title')}
      onCancel={close}
      onOk={() => save.mutate()}
      okText={t('common.save')}
      okButtonProps={{ disabled: !ready }}
      cancelText={t('common.cancel')}
      confirmLoading={save.isPending}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">
        {t('network.userAssignment.intro', { username: user?.username ?? '' })}
      </Typography.Paragraph>

      {loadError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(loadError, t)} />
      ) : null}

      <Form layout="vertical">
        <Form.Item label={t('network.userAssignment.visps')}>
          <Select<string[]>
            mode="multiple"
            allowClear
            loading={visps.isPending || current.isPending}
            value={vispIds}
            onChange={setVispIds}
            optionFilterProp="label"
            placeholder={t('network.userAssignment.none')}
            options={(visps.data ?? []).map((v) => ({ value: v.id, label: `${v.name} · ${v.centerName}` }))}
          />
        </Form.Item>
        <Form.Item label={t('network.userAssignment.centers')} extra={t('network.userAssignment.centersHint')}>
          <Select<string[]>
            mode="multiple"
            allowClear
            loading={centers.isPending || current.isPending}
            value={centerIds}
            onChange={setCenterIds}
            optionFilterProp="label"
            placeholder={t('network.userAssignment.none')}
            options={(centers.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
        </Form.Item>
      </Form>

      {vispIds.length === 0 && centerIds.length === 0 ? (
        <Alert type="info" showIcon message={t('network.userAssignment.emptyMeans')} />
      ) : null}

      {save.isError ? (
        <Alert type="error" showIcon style={{ marginTop: 16 }} message={translateError(save.error, t)} />
      ) : null}
    </Modal>
  )
}
